
import { Component, Vue } from "vue-property-decorator";
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap-vue/dist/bootstrap-vue.css";

@Component({})
export default class NotificationToast extends Vue {
  message: string = "";
  title: string = "";
  variant: string = "info";

  audioContext!: AudioContext;

  mounted() {
    this.initAudio();
    this.requestNotificationPermission();
  }

  initAudio() {
    const AudioContext =
      (window as any).AudioContext || (window as any).webkitAudioContext;
    this.audioContext = new AudioContext();

    document.addEventListener("click", () => {
      if (this.audioContext && this.audioContext.state === "suspended") {
        this.audioContext.resume();
      }
    });
  }

  // Request notification permission
  async requestNotificationPermission() {
    if (!("Notification" in window)) {
      return "denied";
    }

    if (Notification.permission === "default") {
      const permission = await Notification.requestPermission();
      return permission;
    }

    return Notification.permission;
  }

  // Check if app is currently active/visible
  isAppActive(): boolean {
    return !document.hidden;
  }

  // Show browser notification
  showBrowserNotification({
    message,
    title = "お知らせ",
    icon = "/img/logo.c305c7a3.png"
  }: {
    message: string;
    title?: string;
    icon?: string;
  }) {
    if (!("Notification" in window)) {
      return null;
    }

    if (Notification.permission !== "granted") {
      return null;
    }

    const notification = new Notification(title, {
      body: message,
      icon: icon,
      badge: icon,
      tag: "app-notification",
      requireInteraction: false
    });

    // Auto close after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);

    // Handle click - focus window
    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    return notification;
  }

  async showToast({
    message,
    title = "お知らせ",
    variant = "info",
    withLogo = true
  }: {
    message: string;
    title?: string;
    variant?: string;
    withLogo?: boolean;
  }) {
    const isActive = this.isAppActive();
    // If app is not active, show browser notification instead
    if (!isActive) {
      this.showBrowserNotification({ message, title });
      return;
    }

    await this.playSound();
    this.$nextTick(() => {
      const h = this.$createElement;
      const logoNode = withLogo
        ? h("img", {
            attrs: {
              src: "/img/logo.c305c7a3.png",
              width: "32",
              height: "24",
              alt: "施工管理＋α"
            },
            class: "mr-2 rounded"
          })
        : null;

      const textNode = h(
        "span",
        { class: "text-dark font-weight-500" },
        message
      );
      const content = h(
        "div",
        {
          class: "d-flex align-items-center",
          style: { gap: "6px" }
        },
        [logoNode, textNode].filter(Boolean)
      );

      this.$bvToast.toast([content], {
        title: h("span", { class: "text-primary font-weight-bold" }, title),
        variant,
        solid: true,
        toaster: "b-toaster-bottom-right",
        autoHideDelay: 5000,
        appendToast: false,
        noAutoHide: false,
        isStatus: true,
        toastClass: "shadow rounded-lg"
      });
    });
  }

  async playSound() {
    // Create an oscillator node, gain node
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    // Connect the oscillator to the gain node (sound passes through the volume controller)
    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    // Set the frequency to 800 Hz at the current time (a high pitch start)
    oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime);
    oscillator.frequency.setValueAtTime(
      600,
      this.audioContext.currentTime + 0.1
    );

    // Set the initial volume to 0.3 (30% of max volume)
    gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      this.audioContext.currentTime + 0.3
    );

    // Start playing the sound immediately
    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + 0.3);
  }
}
